import { useQuery } from '@tanstack/react-query';
import { searchConsoleApi } from '@/api/searchConsole';
import { queryKeys } from '@/lib/queryKeys';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';

export function useSearchNetwork() {
  return useQuery({ queryKey: queryKeys.searchConsole.network, queryFn: searchConsoleApi.network });
}

/** One site's Search Console, asked again while the worker connects it or reads it. */
export function useSiteSearch(siteId: string) {
  return useQuery({
    queryKey: queryKeys.searchConsole.site(siteId),
    queryFn: () => searchConsoleApi.site(siteId),
    refetchInterval: (query) => {
      const view = query.state.data;
      return view && (view.state === 'requested' || view.state === 'verifying' || view.syncRequested) ? 5_000 : false;
    },
  });
}

const stale = (siteId: string) => () => [queryKeys.searchConsole.site(siteId), queryKeys.searchConsole.network];

export function useConnectSearch(siteId: string, callbacks?: MutationCallbacks<{ requested: boolean }, void>) {
  return useApiMutation(() => searchConsoleApi.connect(siteId), stale(siteId), callbacks);
}

export function useRecheckSearch(siteId: string, callbacks?: MutationCallbacks<{ requested: boolean }, void>) {
  return useApiMutation(() => searchConsoleApi.recheck(siteId), stale(siteId), callbacks);
}

export function useSyncSearch(siteId: string, callbacks?: MutationCallbacks<{ requested: boolean }, void>) {
  return useApiMutation(() => searchConsoleApi.sync(siteId), stale(siteId), callbacks);
}

export function useDisconnectSearch(siteId: string, callbacks?: MutationCallbacks<{ removed: boolean }, void>) {
  return useApiMutation(() => searchConsoleApi.disconnect(siteId), stale(siteId), callbacks);
}
